import React from 'react';

interface LanguageErrorBoundaryProps {
  children: React.ReactNode;
}

interface LanguageErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class LanguageErrorBoundary extends React.Component<
  LanguageErrorBoundaryProps,
  LanguageErrorBoundaryState
> {
  constructor(props: LanguageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  private static isRecoverableError(error: Error): boolean {
    const msg = error?.message ?? "";

    // LanguageContext usage error
    if (msg.includes("useLanguage must be used within")) return true;

    // Common lazy/dynamic import failures (often transient in preview/dev mode)
    if (msg.includes("Failed to fetch dynamically imported module")) return true;
    if (msg.includes("Loading chunk")) return true;
    if (msg.includes("ChunkLoadError")) return true;
    if (msg.includes("Importing a module script failed")) return true;

    return false;
  }

  static getDerivedStateFromError(error: Error): LanguageErrorBoundaryState {
    if (LanguageErrorBoundary.isRecoverableError(error)) {
      return { hasError: true, error };
    }

    // Let other errors bubble up
    return { hasError: false, error: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (!LanguageErrorBoundary.isRecoverableError(error)) return;

    console.error("Recoverable error caught:", error, errorInfo);

    // Auto-reload once for transient dynamic import failures to prevent white screen.
    const msg = error?.message ?? "";
    const isDynamicImportFailure =
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Loading chunk") ||
      msg.includes("ChunkLoadError") ||
      msg.includes("Importing a module script failed");

    if (isDynamicImportFailure) {
      const key = "__lazy_reload_at";
      const last = Number(sessionStorage.getItem(key) || 0);
      const now = Date.now();

      // Prevent reload loops
      if (!last || now - last > 30_000) {
        sessionStorage.setItem(key, String(now));
        setTimeout(() => window.location.reload(), 300);
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center p-6 max-w-md">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-foreground text-lg mb-2">กำลังโหลด...</p>
            <p className="text-muted-foreground text-sm mb-4">
              กำลังรีเฟรชทรัพยากรที่จำเป็น หากยังไม่ขึ้นให้กด “ลองใหม่”
            </p>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              onClick={() => {
                sessionStorage.removeItem("__lazy_reload_at");
                window.location.reload();
              }}
            >
              ลองใหม่
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
